/**
 * Título CP → Revenda (#1216, bloco 1.9). Estado, total, pago e saldo vêm SEMPRE da API
 * (a lista lê o `titulo_status` gravado; o detalhe, `resumo_do_titulo`); o front não soma baixas nem deriva estado/vencimento.
 */
import api from './api'

export type TituloEstado = 'pendente' | 'parcial' | 'quitado' | 'cancelado'

export type TituloResumo = {
  id: string
  estado: TituloEstado
  vencido: boolean
  total: number
  pago: number
  saldo: number
  credito_usado: number
}

export type TituloDaLista = TituloResumo & {
  numero: string
  competencia: string
  revenda_id: string
  revenda_nome: string | null
  cp_id: string
  cp_nome: string | null
  emissao: string | null
  vencimento: string | null
}

export type ListaDeTitulos = {
  titulos: TituloDaLista[]
  total: number
  skip: number
  limit: number
}

export type OrigemDaBaixa = 'manual' | 'pagamento' | 'credito'

export type Baixa = {
  id: string
  invoice_id: string
  amount: number
  payment_date: string
  payment_method: string
  description: string | null
  reference: string | null
  created_by: string | null
  created_at: string
  origem: OrigemDaBaixa
  alocacao_id: string | null
  pagamento_id: string | null
  credito_id: string | null
}

export type ObrigacaoDoCalculo = {
  id: string
  origem: string
  chave_origem: string
  competencia: string
  valor: number | null
}

export type MemoriaDeCalculoDoTitulo = {
  titulo_id: string
  competencia: string
  grupos: {
    origem: string
    quantidade: number
    subtotal: number
    obrigacoes: ObrigacaoDoCalculo[]
  }[]
  total_conhecido: number
  desconhecidas: { quantidade: number; obrigacoes: ObrigacaoDoCalculo[] }
}

export type Credito = {
  id: string
  cp_org_id: string
  revenda_org_id: string
  valor: number
  origem: string
  titulo_id: string | null
  pagamento_id: string | null
  created_at: string
}

export type CreditosDaRevenda = { saldo: number; lancamentos: Credito[] }

export type Alocacao = {
  id: string
  titulo_id: string
  manual_payment_id: string
  valor: number
  saldo_antes: number
  vencimento: string | null
  emissao: string | null
}

export type PagamentoAvulso = {
  pagamento_id: string
  valor: number
  alocacoes: Alocacao[]
  credito: Credito | null
}

export type RevendaDoCp = { id: string; nome: string }

export type PagamentoPayload = {
  amount: string
  payment_date: string
  payment_method: string
  reference?: string | null
  description?: string | null
}

const BASE = '/api/v1/titulos-cp-revenda'

export const titulosCpRevendaApi = {
  async listar(params?: {
    revenda_id?: string
    competencia?: string
    estado?: string
    skip?: number
    limit?: number
  }): Promise<ListaDeTitulos> {
    return (await api.get(BASE, { params })).data
  },
  async revendas(): Promise<RevendaDoCp[]> {
    return (await api.get(`${BASE}/revendas`)).data
  },
  async resumo(id: string): Promise<TituloResumo> {
    return (await api.get(`${BASE}/${id}`)).data
  },
  async baixas(id: string): Promise<Baixa[]> {
    return (await api.get(`${BASE}/${id}/payments`)).data
  },
  async calculo(id: string): Promise<MemoriaDeCalculoDoTitulo> {
    return (await api.get(`${BASE}/${id}/calculo`)).data
  },
  async creditos(revendaId: string): Promise<CreditosDaRevenda> {
    return (await api.get(`${BASE}/creditos/${revendaId}`)).data
  },
  async registrarBaixa(id: string, body: PagamentoPayload): Promise<Baixa> {
    return (await api.post(`${BASE}/${id}/payments`, body)).data
  },
  async registrarPagamentoAvulso(
    body: PagamentoPayload & { revenda_id: string },
  ): Promise<PagamentoAvulso> {
    return (await api.post(`${BASE}/pagamentos`, body)).data
  },
}

export default titulosCpRevendaApi
